/**
 * The documents table (#6559): folder rows above file rows, sortable headers,
 * the lock badge, and — on the page — a row menu with open, open folder,
 * download, unlock and delete. A picker renders the same table with a
 * checkbox per row and no menu.
 */
import React from "react";
import { Tooltip } from "@mui/material";
import CutText, { useCut } from "./CutText";
import AppMark from "../AppMark";
import {
  ArrowSquareOutIcon,
  ArrowsClockwiseIcon,
  CalendarBlankIcon,
  CaretDownIcon,
  CaretRightIcon,
  CaretUpIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  FolderIcon,
  InfoIcon,
} from "@phosphor-icons/react";
import LockedFileIndicator from "@/components/common/LockedFileIndicator";
import UnreadableFileIndicator from "@/components/common/UnreadableFileIndicator";
import FilterCheck from "@/components/FpPages/RMS/Search/Filter/FilterCheck";
import { EM_DASH, Hint, hueFor } from "@/components/FpPages/RMS/design";
import type { FolderEntry } from "@/hooks/useFileTreeV2/fetchFiles";
import {
  getFileIcon,
  isFailedFile,
  isGreyedFile,
  isProcessingFile,
} from "@/hooks/useFileTreeV2/rendering";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import { formatCalendarDay, formatInstant } from "@/utils/temporal";
import type {
  DocumentRow,
  DocumentSortBy,
  DocumentUploader,
  LinkedEntity,
  Places,
} from "./api";
import { locationLabel, locationTrail } from "./places";
import {
  displayFileName,
  documentHref,
  documentPageHref,
  inLink,
  isLocked,
  toRenderable,
  typeLabelOf,
  unmappedTypeWords,
  unreadableLabelOf,
} from "./rows";
import {
  FolderAction,
  downloadDocument,
  targetFromEvent,
  useFolderMenu,
  useRowMenu,
} from "./RowMenu";
import { recordIconKind, recordKindLabel } from "./records";
import type { RowGroup } from "./portals";
import EntityIcon from "@/components/FpPages/RMS/Dashboard/Components/EntityIcon";
import type { BrowserSort } from "./state";

export interface DocumentsTableSelection {
  isSelected: (fileId: string) => boolean;
  toggle: (row: DocumentRow) => void;
  /** Whether a still-processing row may be ticked. The page allows it (bulk
   *  delete of in-flight files, #10770); pickers do not. */
  allowProcessing?: boolean;
  /** Folder ticking (the picker, round 5): a folder row is selectable, and
   *  a folder and the documents inside it are mutually exclusive — the
   *  host says which side is currently open. */
  isFolderSelected?: (folder: FolderEntry) => boolean;
  toggleFolder?: (folder: FolderEntry) => void;
  canSelectFolder?: (folder: FolderEntry) => boolean;
  canSelectFile?: (row: DocumentRow) => boolean;
  /** The name of the ticked folder a document sits in, when one does
   *  (#12088, Hunter): such a row reads as selected — a checked tick it
   *  cannot toggle, the selected wash, and the folder's name on hover —
   *  rather than as a bare disabled box that says nothing about why. */
  coveringFolder?: (row: DocumentRow) => string | null;
  /** Select all (round 10): the header checkbox sets every listed selectable
   *  file at once — files only, never folders — and the browser's
   *  "Select all N" line pages the rest in and sets those too. */
  setAll?: (rows: DocumentRow[], selected: boolean) => void;
}

/** Whether a row may be ticked: a processing document is not, unless it is
 *  merely locked or the surface allows it, and the host may refuse a file
 *  whose folder is already ticked. One rule for the row, the header and the
 *  select-all line. */
export const rowSelectable = (
  row: DocumentRow,
  selection: DocumentsTableSelection
): boolean =>
  (!isProcessingFile(toRenderable(row)) ||
    isLocked(row) ||
    (selection.allowProcessing ?? false)) &&
  (selection.canSelectFile?.(row) ?? true);

export interface DocumentsTableProps {
  rows: DocumentRow[];
  folders: FolderEntry[];
  /** The app a folder is a connection's root for (round 18g): its row
   *  carries the app's mark in place of the folder glyph. */
  folderApp?: (folder: FolderEntry) => string | null;
  /** What to call a folder instead of its last path segment, where the
   *  folder is a connection's root (#12074); null keeps the segment. */
  folderName?: (folder: FolderEntry) => string | null;
  showLocation: boolean;
  /** The places a location is named from (round 19, Claude Design Q2): a
   *  row's Location is the trail from its place — the tree, its drive, or
   *  its connection — and at the place's root, the place itself. Without
   *  the places read the place is the tree. */
  places?: Places;
  sort: BrowserSort;
  onSortChange: (sort: BrowserSort) => void;
  /** Opens a folder. A folder row names its drive (#12088): a search's
   *  folder hits come from every drive the search spanned, and opening one
   *  is arriving in its drive; the browser ignores a drive it is already in. */
  onOpenFolder: (
    path: string[],
    focus?: string,
    driveId?: string | null
  ) => void;
  onOpenFile: (row: DocumentRow) => void;
  selection?: DocumentsTableSelection;
  rowActions: boolean;
  typeLabels: Record<string, string>;
  isLoading: boolean;
  /** The rows on screen are the previous answer standing in while the next
   *  one loads (#11635 slice 1, Round 15, R52: a sync's narrowing applied,
   *  moved or cleared): they stay, dimmed and marked busy, rather than
   *  giving way to the skeleton. */
  stale?: boolean;
  isError: boolean;
  onRowsChanged: () => void;
  /** The row the details pane is describing, given the selected-row wash so
   *  the pairing between pane and row is never in question. */
  activeFileId?: string | null;
  /** A row the arrival named — "Show in folder" lands here. It is scrolled
   *  into view and marked, because a folder of hundreds that does not say
   *  which row you asked about has answered a different question. */
  focusFileId?: string | null;
  /** The row menu's Details item (page only). */
  onDetails?: (row: DocumentRow) => void;
  /** The row menu's Move item (page only, where the reader can edit). */
  onMove?: (row: DocumentRow) => void;
  /** Folder rows get a ⋮ with Rename / Download / Delete when the owner
   *  handles them. */
  onFolderAction?: (action: FolderAction, path: string[]) => void;
  /** Record view with Include vehicle documents on: which vehicle a row came
   *  from, shown as its own column (the roll-up's reason for being). */
  /** The records a document belongs to, as a column (#6559 round 7). Two
   *  headings, one column: **Related to** in a flat or recursive listing and
   *  in search results, where it is the shortest path anyone will use, and
   *  **Also linked to** inside a record location, where it carries the
   *  many-to-many fact the viewer of a record needs. Absent inside a folder,
   *  where the value is nearly constant: 83% to 99% of the documents under
   *  `Managers/Acme` belong to Acme, so a column repeating it earns nothing.
   *
   *  It names up to three records and collapses the rest to a count. At one
   *  organization 82% of linked documents touch two or three records and 0.2%
   *  touch four or more, and the pair is almost always a manager and one of
   *  its vehicles — a legible pair, not the ambiguity a single name plus "+3"
   *  would be. */
  recordColumn?: {
    label: string;
    of: (row: DocumentRow) => { records: LinkedEntity[]; total: number };
  };
  onOpenRecord?: (record: LinkedEntity) => void;
  /** Phone: the rows as a compact list — icon, name, one meta line, ⋮ — in
   *  place of the table, which cannot fit a phone without panning sideways
   *  (rules/frontend.md, small screens). */
  compact?: boolean;
  /** Home's suggestions: why each row is here, shown in a Reason column
   *  after the name (Drive's "Reason suggested"). The reason carries the
   *  date, so the two date columns stand down and, the order being signal
   *  recency, the headers do not offer a sort. */
  reasons?: Record<string, string>;
  /** The reason column's header; "Reason" unless the host says otherwise
   *  (Home's Recently added reads "Added": who and when). */
  reasonsLabel?: string;
  /** Home's lists carry no type (round 18d, Hunter, Drive's shape). */
  hideType?: boolean;
  /** A table sharing a card with other sections (Home; round 18f, Claude
   *  Design): its header is plain labels on the card ground with one
   *  hairline under, neither filled nor sticky — the fill earns its keep
   *  only floating over a long scrolling listing. The host insets the
   *  table to its gutter. */
  embedded?: boolean;
  /** An embedded table directly under a section heading whose gap already
   *  spaces it: its header adds no top padding (the portals band, #11635
   *  slice 1, R33). */
  flushHeader?: boolean;
  /** A narrow card (the destinations column and a details pane beside it):
   *  the uploader moves into the Uploaded cell's tooltip, Location shortens
   *  and the hover strip stands down, so the table fits without panning. */
  dense?: boolean;
  /** The selection surface (#6559 round 8, Claude Design): three fixed
   *  columns — Name, Uploaded, Type — identical in every listing, so nothing
   *  jumps and nothing overflows. The location sits under the name in a
   *  recursive listing rather than in a column; Uploaded is the date alone;
   *  a folder row is only its name; the name is plain text, the row is the
   *  tick, and a quiet Open at the row's end is the side door to the viewer
   *  in a new tab. A processing row says "Processing" where its type would
   *  go, and a failed one "Processing failed" (#13887). */
  picker?: boolean;
  /** What an empty listing says. The browser knows why the listing is empty
   *  — a search with no hits, a filter, an empty window — and what control
   *  would fix it; the table only knows it has no rows. */
  emptyState?: React.ReactNode;
  /** An empty listing that has landed draws no header row, only its empty
   *  state (#11635 Round 16, R62: the portals view). While it loads the
   *  header stays, so the skeleton rows keep their shape. */
  hideHeaderWhenEmpty?: boolean;
  /** Group headers between the rows (#11635 Round 4, spec R20): the rows
   *  arrive already ordered by group, and a header row is drawn wherever the
   *  group changes — the record's glyph, its name and its count, a link to
   *  the record's location where it has one. */
  groups?: {
    of: (row: DocumentRow) => RowGroup;
    onOpenRecord?: (record: LinkedEntity) => void;
    /** Round 5 (spec R3): a group folds. Absent, every group is open and
     *  the header carries no disclosure. */
    isOpen?: (key: string) => boolean;
    onToggle?: (key: string) => void;
    /** What the header says beside the count: the group's rows are what
     *  is loaded of it. */
    decorate?: (group: RowGroup, rows: DocumentRow[]) => React.ReactNode;
  };
}

/** The rows a group header sits above: the first row and every row whose
 *  group differs from the one before it, with the group's size. */
const groupStarts = (
  rows: DocumentRow[],
  of: (row: DocumentRow) => RowGroup
): Map<string, { group: RowGroup; count: number; rows: DocumentRow[] }> => {
  const starts = new Map<
    string,
    { group: RowGroup; count: number; rows: DocumentRow[] }
  >();
  const members = new Map<string, DocumentRow[]>();
  for (const row of rows) {
    const group = of(row);
    const bucket = members.get(group.key);
    if (bucket) bucket.push(row);
    else members.set(group.key, [row]);
  }
  let previous: string | null = null;
  for (const row of rows) {
    const group = of(row);
    if (group.key !== previous) {
      const own = members.get(group.key) ?? [];
      starts.set(row.fileId, { group, count: own.length, rows: own });
    }
    previous = group.key;
  }
  return starts;
};

const groupCount = (count: number): string =>
  `${count} document${count === 1 ? "" : "s"}`;

/** A group header's content: the glyph, the name (a link to the record's
 *  location where the group is a record), and the count. */
const GroupHeading: React.FC<{
  group: RowGroup;
  count: number;
  onOpenRecord?: (record: LinkedEntity) => void;
  /** The disclosure (Round 5, R3): absent, the group does not fold. */
  open?: boolean;
  onToggle?: () => void;
  decoration?: React.ReactNode;
}> = ({ group, count, onOpenRecord, open, onToggle, decoration }) => {
  const glyph =
    group.kind === "record" ? (
      <EntityIcon
        kind={recordIconKind(group.record.kind)}
        size={16}
        aria-hidden
        className="shrink-0 text-[var(--fp-ink-2)]"
      />
    ) : group.kind === "folder" ? (
      <FolderIcon
        size={16}
        weight="fill"
        aria-hidden
        className="shrink-0 text-[var(--fp-ink-2)]"
      />
    ) : group.kind === "day" ? (
      <CalendarBlankIcon
        size={16}
        aria-hidden
        className="shrink-0 text-[var(--fp-ink-2)]"
      />
    ) : (
      <ArrowsClockwiseIcon
        size={16}
        aria-hidden
        className="shrink-0 text-[var(--fp-ink-2)]"
      />
    );
  const name =
    group.kind === "record" && onOpenRecord ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenRecord(group.record);
        }}
        className="truncate text-left font-medium text-[var(--fp-ink)] hover:underline"
      >
        {group.label}
      </button>
    ) : (
      <span className="truncate font-medium text-[var(--fp-ink)]">
        {group.label}
      </span>
    );
  const disclosure =
    onToggle !== undefined ? (
      <button
        type="button"
        aria-label={`${open ? "Collapse" : "Expand"} ${group.label}`}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-[var(--fp-ink-3)] hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)]"
      >
        {open ? (
          <CaretDownIcon size={14} aria-hidden />
        ) : (
          <CaretRightIcon size={14} aria-hidden />
        )}
      </button>
    ) : null;
  return (
    <span
      className={`flex min-w-0 items-center gap-2 text-[13px] ${
        onToggle !== undefined ? "cursor-pointer" : ""
      }`}
      onClick={onToggle}
    >
      {disclosure}
      {glyph}
      {name}
      <span className="shrink-0 text-[12.5px] text-[var(--fp-ink-3)]">
        · {groupCount(count)}
      </span>
      {decoration}
    </span>
  );
};

/** The uploader in the Uploaded cell, capped so no sender can widen the
 *  table (#12075). Its tooltip follows CutText's ruling — only what the
 *  visible text leaves out: the full label once the cap cuts it, and the
 *  sender's address for an email-ingested upload, whose label is the name
 *  alone. A short user or connector label gets none. */
const UploaderName: React.FC<{ uploader: DocumentUploader }> = ({
  uploader,
}) => {
  const [ref, cut] = useCut();
  const address = uploader.address ?? null;
  const title = address
    ? cut
      ? `${uploader.label} <${address}>`
      : address
    : cut
      ? uploader.label
      : "";
  return (
    <Tooltip title={title} placement="top">
      <span ref={ref} className="max-w-[200px] truncate">
        {uploader.label}
      </span>
    </Tooltip>
  );
};

/** A type tag that truncates inside its own pill (CD sign-off note 1): the
 *  design system's TagChip in shape and hue, with an ellipsis on the label
 *  and the full type in the tooltip, so a long type never clips mid-word at
 *  the column edge. */
const typeTagStyle: React.CSSProperties = {
  display: "inline-block",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  fontFamily: "var(--fp-font-ui)",
  letterSpacing: "var(--fp-track-body)",
  fontSize: "var(--fp-t-fine)",
  fontWeight: "var(--fp-w-strong)" as unknown as number,
  lineHeight: "var(--fp-lh-body)",
  padding: "var(--fp-gap-hair) var(--fp-gap-tight)",
  borderRadius: "var(--fp-chip-r)",
  border: "1px solid transparent",
};

/** The pill's tooltip is its label, and only while the pill is cut (round
 *  18c, Claude Design): the type key is internal and appears in no title,
 *  aria-label or alt. */
export const TypeTag: React.FC<{ label: string }> = ({ label }) => {
  const hue = hueFor(label);
  return (
    <CutText
      text={label}
      style={{
        ...typeTagStyle,
        color: `var(--fp-tag-${hue})`,
        backgroundColor: `var(--fp-tag-${hue}-bg)`,
      }}
    />
  );
};

/** A document type key the organization's vocabulary no longer names: shown
 *  as its words in a neutral, hueless tag so it reads as unmapped rather than
 *  as a real type, with the raw key in the tooltip (CD ruling J). */
export const UnmappedTypeTag: React.FC<{ typeKey: string }> = ({ typeKey }) => {
  // The words, never the key (round 18c).
  const words = unmappedTypeWords(typeKey);
  return (
    <CutText
      text={words}
      style={{
        ...typeTagStyle,
        color: "var(--fp-ink-2)",
        backgroundColor: "var(--fp-ink-wash)",
      }}
    />
  );
};

/** The one way a type key becomes a pill: the organization's label in its
 *  hue, or the unmapped treatment. Every surface goes through this so Home,
 *  the grid, the pane and the table never disagree (CD ruling J). */
export const TypeTagFor: React.FC<{
  typeKey: string;
  typeLabels: Record<string, string>;
}> = ({ typeKey, typeLabels }) =>
  typeLabels[typeKey] ? (
    <TypeTag label={typeLabels[typeKey]} />
  ) : (
    <UnmappedTypeTag typeKey={typeKey} />
  );

/** A document's types on one line (round 19f, Hunter: stacked pills made
 *  the row twice as tall): the first type's pill and, past it, a "+N" that
 *  names the rest on hover — the Related to column's own grammar. */
export const TypeTags: React.FC<{
  types: string[];
  typeLabels: Record<string, string>;
}> = ({ types, typeLabels }) => {
  const [first, ...rest] = types.filter((type) => type !== "empty");
  if (first === undefined)
    return <span className="text-[var(--fp-ink-3)]">{EM_DASH}</span>;
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <TypeTagFor typeKey={first} typeLabels={typeLabels} />
      {rest.length > 0 && (
        <Tooltip
          title={rest.map((type) => typeLabelOf(type, typeLabels)).join(", ")}
          placement="top"
        >
          <span className="shrink-0 text-[11.5px] text-[var(--fp-ink-3)]">
            +{rest.length}
          </span>
        </Tooltip>
      )}
    </span>
  );
};

/** The records a document belongs to, as names rather than a count: "+3"
 *  points nowhere, and the point of the column is which records these are.
 *
 *  Up to three, because the pair is the common case — 82% of linked documents
 *  touch two or three records, almost always a manager and one of its
 *  vehicles — and anything beyond that collapses to a count rather than
 *  spending width on ambiguity (#6559 round 7).
 */
export const RECORD_CHIP_LIMIT = 3;

export const RecordChips: React.FC<{
  records: LinkedEntity[];
  /** Every record the document touches, including those beyond the handful
   *  the row carries — the count is the fact, so it is never the length of a
   *  capped list. */
  total: number;
  onOpenRecord?: (record: LinkedEntity) => void;
}> = ({ records, total, onOpenRecord }) => {
  if (records.length === 0)
    return <span className="text-[var(--fp-ink-3)]">{EM_DASH}</span>;
  const shown = records.slice(0, RECORD_CHIP_LIMIT);
  const hidden = records.slice(RECORD_CHIP_LIMIT);
  const beyond = Math.max(total - shown.length, hidden.length);
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {shown.map((record) => (
        <Tooltip
          key={record.publicId}
          title={`${recordKindLabel(record.kind)} · ${record.name}`}
          placement="top"
        >
          {onOpenRecord ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpenRecord(record);
              }}
              className="min-w-0 truncate rounded-[5px] border border-[var(--fp-border-strong)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--fp-ink-2)] hover:border-[var(--fp-ink)] hover:text-[var(--fp-ink)]"
            >
              {record.name}
            </button>
          ) : (
            <span className="min-w-0 truncate rounded-[5px] border border-[var(--fp-border-strong)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--fp-ink-2)]">
              {record.name}
            </span>
          )}
        </Tooltip>
      ))}
      {beyond > 0 && (
        <Tooltip
          title={
            hidden.length === beyond
              ? hidden.map((r) => r.name).join(", ")
              : `${hidden.map((r) => r.name).join(", ")} and ${beyond - hidden.length} more`
          }
          placement="top"
        >
          <span className="shrink-0 text-[11.5px] text-[var(--fp-ink-3)]">
            +{beyond}
          </span>
        </Tooltip>
      )}
    </span>
  );
};

const TH_BASE =
  "whitespace-nowrap px-3 py-2 text-left text-[11.5px] font-medium uppercase tracking-wide text-[var(--fp-ink-3)] select-none";
/** The listing's header floats over the rows on the band. */
const TH_FLOATING = `sticky top-0 z-[1] bg-[var(--fp-band)] ${TH_BASE}`;
/** An embedded table's header (round 18f): plain labels, one hairline. */
const TH_EMBEDDED = `border-b border-[var(--fp-border)] ${TH_BASE}`;
/** The same under a section heading whose gap already spaces it: no top
 *  padding of its own (the portals band, #11635 slice 1, R33). */
const TH_EMBEDDED_FLUSH = `${TH_EMBEDDED} pt-0`;
const tdClass =
  "whitespace-nowrap px-3 py-2 text-[13px] text-[var(--fp-ink)] align-middle";

interface SortableHeaderProps {
  label: string;
  column: DocumentSortBy;
  sort: BrowserSort;
  onSortChange: (sort: BrowserSort) => void;
  /** False renders a plain header: a list ordered by something other than a
   *  column must not offer to sort by one. */
  sortable?: boolean;
  className?: string;
  /** The header cell's class: floating on the band, or embedded. */
  thClass: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  column,
  sort,
  onSortChange,
  sortable = true,
  className = "",
  thClass,
}) => {
  if (!sortable) {
    return (
      <th scope="col" className={`${thClass} ${className}`}>
        {label}
      </th>
    );
  }
  const active = sort.by === column;
  const next = (): BrowserSort =>
    active
      ? { by: column, order: sort.order === "asc" ? "desc" : "asc" }
      : { by: column, order: column === "name" ? "asc" : "desc" };
  return (
    <th
      scope="col"
      aria-sort={
        active ? (sort.order === "asc" ? "ascending" : "descending") : "none"
      }
      tabIndex={0}
      onClick={() => onSortChange(next())}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSortChange(next());
        }
      }}
      className={`${thClass} cursor-pointer hover:text-[var(--fp-ink)] ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active &&
          (sort.order === "asc" ? (
            <CaretUpIcon size={11} weight="bold" aria-hidden />
          ) : (
            <CaretDownIcon size={11} weight="bold" aria-hidden />
          ))}
      </span>
    </th>
  );
};

const RowCheck: React.FC<{
  checked: boolean;
  /** Some but not all (the select-all header): fills like checked, reads
   *  as mixed. */
  mixed?: boolean;
  disabled?: boolean;
  label: string;
  onToggle: () => void;
}> = ({ checked, mixed = false, disabled = false, label, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={mixed ? "mixed" : checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    className="flex h-6 w-6 items-center justify-center rounded-[5px] disabled:opacity-40"
  >
    <FilterCheck checked={checked} mixed={mixed} />
  </button>
);

/** A document's tick, which says on hover which ticked folder selected it
 *  when one did (#12088, Hunter). The tooltip wraps a span so the tick
 *  keeps its own accessible name. */
const CoveredCheck: React.FC<{
  covering: string | null;
  checked: boolean;
  disabled: boolean;
  label: string;
  onToggle: () => void;
}> = ({ covering, checked, disabled, label, onToggle }) =>
  covering === null ? (
    <RowCheck
      checked={checked}
      disabled={disabled}
      label={label}
      onToggle={onToggle}
    />
  ) : (
    <Tooltip title={`Selected with ${covering}`} placement="top">
      <span className="inline-flex">
        <RowCheck
          checked={checked}
          disabled={disabled}
          label={label}
          onToggle={onToggle}
        />
      </span>
    </Tooltip>
  );

/** The tick column's header (round 10): every selectable listed file at
 *  once. Indeterminate with some ticked, checked with all; a click on either
 *  completes the set, a click on a full set clears it. */
const SelectAllCheck: React.FC<{
  rows: DocumentRow[];
  selection: DocumentsTableSelection;
  setAll: (rows: DocumentRow[], selected: boolean) => void;
}> = ({ rows, selection, setAll }) => {
  const pickable = rows.filter((row) => rowSelectable(row, selection));
  const picked = pickable.filter((row) => selection.isSelected(row.fileId));
  const all = pickable.length > 0 && picked.length === pickable.length;
  return (
    <RowCheck
      checked={all}
      mixed={picked.length > 0 && !all}
      disabled={pickable.length === 0}
      label="Select all"
      onToggle={() => setAll(pickable, !all)}
    />
  );
};

// --- the table -----------------------------------------------------------------

const quickActionClass =
  "rounded-full p-1.5 text-[var(--fp-ink-3)] opacity-0 transition-opacity hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)] focus-visible:opacity-100 group-hover:opacity-100";

/** The trail on hover, Drive's shape (round 19f, Hunter): a dark pill of
 *  crumbs, a folder glyph on each, chevrons between, the document's own
 *  folder last and lifted; every crumb opens its folder. */
const TrailPill: React.FC<{
  trail: string[];
  onOpenCrumb: (index: number) => void;
}> = ({ trail, onOpenCrumb }) => (
  <span className="flex items-center gap-0.5">
    {trail.map((crumb, index) => {
      const last = index === trail.length - 1;
      return (
        <React.Fragment key={`${index}:${crumb}`}>
          {index > 0 && (
            <CaretRightIcon
              size={14}
              weight="bold"
              aria-hidden
              className="shrink-0 text-[var(--fp-surface)] opacity-70"
            />
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenCrumb(index);
            }}
            className={`flex max-w-[240px] items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-medium text-[var(--fp-surface)] hover:bg-white/15 ${
              last ? "bg-white/20" : ""
            }`}
          >
            {index > 0 && (
              <FolderIcon
                size={15}
                weight="fill"
                aria-hidden
                className="shrink-0"
              />
            )}
            <span className="truncate">{crumb}</span>
          </button>
        </React.Fragment>
      );
    })}
  </span>
);

/** The pill's paper: ink ground, fully rounded, sized to its crumbs. */
const trailPillSlotProps = {
  tooltip: {
    sx: {
      maxWidth: "none",
      padding: "6px",
      borderRadius: "9999px",
      backgroundColor: "var(--fp-ink)",
      boxShadow: "var(--fp-lift)",
    },
  },
};

/** A location is the folder the document sits in (round 19e, Hunter:
 *  Drive's shape), with a folder glyph; hovering it shows the whole trail
 *  from the place as a pill of crumbs, each of them a door (round 19f).
 *  At a place's root it is the place's name, with nothing to add. Opening
 *  the cell opens the row's folder. */
const LocationCell: React.FC<{
  trail: string[];
  dense: boolean;
  onOpen: () => void;
  onOpenCrumb: (index: number) => void;
}> = ({ trail, dense, onOpen, onOpenCrumb }) => {
  const [ref, cut] = useCut();
  const inFolder = trail.length > 1;
  const label = trail[trail.length - 1] ?? "";
  const button = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      className={`${dense ? "max-w-[150px]" : "max-w-[260px]"} flex items-center gap-1.5 text-left hover:underline`}
    >
      {inFolder && (
        <FolderIcon
          size={14}
          weight="fill"
          aria-hidden
          className="shrink-0 text-[var(--fp-ink-3)]"
        />
      )}
      <span ref={ref} className="block min-w-0 truncate">
        {label}
      </span>
    </button>
  );
  // Always the same element in this position, so the button is never
  // remounted. In a folder the title is the pill; at a place's root it is
  // the place's name only while the cell cuts it (round 18c's rule), else
  // none.
  const pill = inFolder;
  return (
    <Tooltip
      title={
        pill ? (
          <TrailPill trail={trail} onOpenCrumb={onOpenCrumb} />
        ) : cut ? (
          label
        ) : null
      }
      placement="top"
      slotProps={pill ? trailPillSlotProps : undefined}
    >
      {button}
    </Tooltip>
  );
};

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  rows,
  folders,
  showLocation,
  places,
  sort,
  onSortChange,
  onOpenFolder,
  onOpenFile,
  selection,
  rowActions,
  typeLabels,
  isLoading,
  stale = false,
  isError,
  onRowsChanged,
  activeFileId = null,
  focusFileId = null,
  onDetails,
  onMove,
  onFolderAction,
  recordColumn,
  onOpenRecord,
  compact = false,
  dense = false,
  folderApp,
  folderName,
  reasons,
  reasonsLabel,
  hideType = false,
  embedded = false,
  flushHeader = false,
  picker = false,
  emptyState,
  groups,
  hideHeaderWhenEmpty = false,
}) => {
  const zone = useViewerTimeZone();
  // The previous answer, while it stands in for the next one, is dimmed
  // (R52); the fade is CSS, nothing times it.
  const staleClass = `transition-opacity duration-150 ${
    stale ? "opacity-50" : "opacity-100"
  }`;
  const headers = React.useMemo(
    () => (groups ? groupStarts(rows, groups.of) : null),
    [groups, rows]
  );
  // One trail per address per places read: resolving a place walks the
  // connections, and a row is asked for its trail more than once a render.
  const trailOf = React.useMemo(() => {
    const cache = new Map<string, string[]>();
    return (row: DocumentRow): string[] => {
      const key = JSON.stringify([row.driveId, row.filePath]);
      let trail = cache.get(key);
      if (trail === undefined) {
        trail = locationTrail(places, row.driveId, row.filePath);
        cache.set(key, trail);
      }
      return trail;
    };
  }, [places]);
  // A folder hit's location (#12088): the trail to its parent, since a name
  // alone is ambiguous across `Managers/` and `Vehicles/`. Only a recursive
  // listing shows it; a browse listing's folders are all in the open folder.
  const folderTrailOf = React.useCallback(
    (folder: FolderEntry): string[] =>
      locationTrail(places, folder.driveId, folder.filePath.slice(0, -1)),
    [places]
  );
  const openFolderCrumb = (folder: FolderEntry, index: number) => {
    const parent = folder.filePath.slice(0, -1);
    const trail = folderTrailOf(folder);
    // Crumb i is the place (0) or the i-th folder under its root: the
    // path's head, the root's length plus i.
    onOpenFolder(
      parent.slice(0, parent.length - (trail.length - 1) + index),
      undefined,
      folder.driveId
    );
  };
  const rowMenu = useRowMenu({
    onOpenFile,
    onOpenFolder,
    onDetails,
    onMove,
    showLocation,
    onRowsChanged,
  });
  const folderMenu = useFolderMenu(onOpenFolder, onFolderAction);

  // Scroll the row the arrival named into view, once it exists. "Show in
  // folder" can land in a folder of hundreds, and pointing at a row below the
  // fold is the same as not pointing at one (Hunter, round 7g). Keyed on the
  // id so a second arrival at the same folder still scrolls.
  const focusRef = React.useRef<HTMLTableRowElement | null>(null);
  React.useEffect(() => {
    if (!focusFileId) return;
    focusRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focusFileId, rows]);

  // A picker's row is a choice, so a click there still ticks it. Everywhere
  // the row carries its own actions — the page, Home — the row is the
  // document. The page has a selection too (for bulk delete), but it is made
  // in the tick column, so selection cannot be the discriminator.
  const openOnRowClick = rowActions;

  if (compact) {
    // The phone follows the desktop (Claude Design, round 7f): a row is the
    // document, and that is a fact about the object rather than a property of
    // the screen. A tap opens it; Details is in the row's sheet. In a picker
    // the tap is still the tick.
    // Whether the tap ticks. A picker's tap is the tick; the page's tap opens
    // the document, and its ticks are made in the tick column.
    const picking = selection !== undefined && !rowActions;
    // Whether a tick column exists at all. It exists wherever the host has a
    // selection — the page included, which is what makes a selection (and so
    // the phone's selection bar) reachable on a phone at all: gating the
    // column on `picking` meant the page showed no tick affordance below
    // 720px, so nothing could ever be selected there (round 7h review B2).
    const ticking = selection !== undefined;
    const tap = picking
      ? (row: DocumentRow) => {
          if (rowSelectable(row, selection)) selection.toggle(row);
        }
      : onOpenFile;
    const metaOf = (row: DocumentRow): string => {
      const types = row.documentTypes.filter((t) => t !== "empty");
      return [
        reasons?.[row.fileId] ?? null,
        isLocked(row)
          ? "Locked"
          : (unreadableLabelOf(row) ??
            (types.length
              ? types.map((t) => typeLabelOf(t, typeLabels)).join(", ")
              : null)),
        // A document date is the document's own day; an upload is an instant
        // and says so, so two adjacent rows never show unlike dates alike.
        row.documentDate
          ? formatCalendarDay(row.documentDate, "medium")
          : row.uploadedAt
            ? `Uploaded ${formatInstant(row.uploadedAt, zone, "medium")}`
            : null,
        showLocation ? locationLabel(trailOf(row)) : null,
      ]
        .filter(Boolean)
        .join(" · ");
    };
    return (
      <>
        <ul
          className={`m-0 list-none p-0 ${staleClass}`}
          aria-busy={isLoading || stale}
          data-stale={stale ? "true" : undefined}
        >
          {!isLoading &&
            folders.map((folder) => {
              const name =
                folderName?.(folder) ??
                folder.filePath[folder.filePath.length - 1] ??
                "";
              return (
                <li
                  key={`folder:${folder.driveId ?? ""}:${JSON.stringify(folder.filePath)}`}
                  data-selected={
                    (selection?.isFolderSelected?.(folder) ?? false) ||
                    undefined
                  }
                  // The phone is where selection became possible at all, so it
                  // is the last place a ticked row should be marked by the tick
                  // alone (round 7i review).
                  className={`flex min-h-[56px] items-center gap-3 border-b border-[var(--fp-border)] pl-4 pr-1 ${
                    selection?.isFolderSelected?.(folder)
                      ? "bg-[var(--fp-accent-soft)]"
                      : ""
                  }`}
                >
                  {ticking && selection.toggleFolder && (
                    <RowCheck
                      checked={selection.isFolderSelected?.(folder) ?? false}
                      disabled={!(selection.canSelectFolder?.(folder) ?? true)}
                      label={name}
                      onToggle={() => selection.toggleFolder?.(folder)}
                    />
                  )}
                  <button
                    type="button"
                    // Named by the folder alone: an app mark inside is named
                    // too, and would otherwise join the name.
                    aria-label={name}
                    onClick={() =>
                      onOpenFolder(folder.filePath, undefined, folder.driveId)
                    }
                    className="flex min-w-0 flex-1 items-center gap-3 py-2 text-left"
                  >
                    {folderApp?.(folder) ? (
                      <AppMark app={folderApp(folder)!} size={22} />
                    ) : (
                      <FolderIcon
                        size={22}
                        weight="fill"
                        className="shrink-0 text-[var(--fp-ink-3)]"
                        aria-hidden
                      />
                    )}
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-[14px] font-medium text-[var(--fp-ink)]">
                        {name}
                      </span>
                      {showLocation ? (
                        // A search's folder hit says where it is (#12088),
                        // as a file hit does in the row below it.
                        <span className="truncate text-[12px] text-[var(--fp-ink-3)]">
                          {locationLabel(folderTrailOf(folder))}
                        </span>
                      ) : (
                        folder.documentDate && (
                          <span className="truncate text-[12px] text-[var(--fp-ink-3)]">
                            {formatCalendarDay(folder.documentDate, "medium")}
                          </span>
                        )
                      )}
                    </span>
                  </button>
                  {rowActions && (
                    <button
                      type="button"
                      aria-label={`Actions for ${name}`}
                      onClick={(e) =>
                        folderMenu.open(targetFromEvent(folder.filePath, e))
                      }
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--fp-ink-3)] hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)]"
                    >
                      <DotsThreeIcon size={20} weight="bold" aria-hidden />
                    </button>
                  )}
                </li>
              );
            })}
          {!isLoading &&
            rows.map((row) => {
              const processing = isProcessingFile(toRenderable(row));
              const greyed = isGreyedFile(toRenderable(row));
              const active =
                row.fileId === activeFileId || row.fileId === focusFileId;
              // Selected with its ticked folder (#12088): reads as selected.
              const covering = selection?.coveringFolder?.(row) ?? null;
              const selected =
                (selection?.isSelected(row.fileId) ?? false) ||
                covering !== null;
              const header = headers?.get(row.fileId);
              const closed =
                groups?.isOpen !== undefined &&
                !groups.isOpen(groups.of(row).key);
              if (closed && !header) return null;
              return (
                <React.Fragment key={row.fileId}>
                  {header && (
                    <li
                      data-group-header
                      className="flex min-h-[40px] items-center border-b border-[var(--fp-border)] bg-[var(--fp-band)] px-4 py-2"
                    >
                      <GroupHeading
                        group={header.group}
                        count={header.count}
                        onOpenRecord={groups?.onOpenRecord}
                        open={groups?.isOpen?.(header.group.key)}
                        onToggle={
                          groups?.onToggle
                            ? () => groups.onToggle?.(header.group.key)
                            : undefined
                        }
                        decoration={groups?.decorate?.(
                          header.group,
                          header.rows
                        )}
                      />
                    </li>
                  )}
                  {!closed && (
                    <li
                      data-active={active || undefined}
                      data-selected={selected || undefined}
                      onClick={() => tap(row)}
                      className={`flex min-h-[56px] items-center gap-3 border-b border-[var(--fp-border)] pl-4 pr-1 ${
                        selected
                          ? "bg-[var(--fp-accent-soft)]"
                          : active
                            ? "bg-[var(--fp-ink-wash)]"
                            : ""
                      }`}
                    >
                      {ticking && (
                        <CoveredCheck
                          covering={covering}
                          checked={selected}
                          disabled={
                            !(
                              (!processing ||
                                isLocked(row) ||
                                selection.allowProcessing) &&
                              (selection.canSelectFile?.(row) ?? true)
                            )
                          }
                          label={displayFileName(row.fileName)}
                          onToggle={() => selection.toggle(row)}
                        />
                      )}
                      <span className="shrink-0 [&_svg]:h-[22px] [&_svg]:w-[22px]">
                        {getFileIcon(toRenderable(row), greyed)}
                      </span>
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            tap(row);
                          }
                        }}
                        className="flex min-w-0 flex-1 flex-col py-2 text-left"
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span
                            className={`truncate text-[14px] ${
                              greyed
                                ? "text-[var(--fp-ink-3)]"
                                : "font-medium text-[var(--fp-ink)]"
                            }`}
                          >
                            {displayFileName(row.fileName)}
                          </span>
                          {isLocked(row) && (
                            // The passive mark on the name's line (round 12,
                            // #11856): it says the file is locked and nothing
                            // else; opening the file is where it is unlocked.
                            <span
                              data-testid="row-lock"
                              className="inline-flex shrink-0 items-center leading-none"
                            >
                              <LockedFileIndicator
                                status={row.status ?? undefined}
                              />
                            </span>
                          )}
                          {row.unreadableReason && (
                            // A file we cannot read says why on its name's
                            // line (#11277); a password does not open it, so
                            // the mark is not the lock's unlock path.
                            <span
                              data-testid="row-unreadable"
                              data-reason={row.unreadableReason}
                              className="inline-flex shrink-0 items-center leading-none"
                            >
                              <UnreadableFileIndicator
                                reason={row.unreadableReason}
                                label={unreadableLabelOf(row) ?? ""}
                              />
                            </span>
                          )}
                        </span>
                        <span
                          data-testid="row-meta"
                          className="truncate text-[12px] text-[var(--fp-ink-3)]"
                        >
                          {metaOf(row) || EM_DASH}
                        </span>
                      </div>
                      {rowActions && (
                        <button
                          type="button"
                          aria-label={`Actions for ${displayFileName(row.fileName)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            rowMenu.open(targetFromEvent(row, e));
                          }}
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--fp-ink-3)] hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)]"
                        >
                          <DotsThreeIcon size={20} weight="bold" aria-hidden />
                        </button>
                      )}
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          {!isLoading &&
            !isError &&
            rows.length === 0 &&
            folders.length === 0 && (
              <li className="px-4 py-10 text-center text-[13px] text-[var(--fp-ink-3)]">
                No documents
              </li>
            )}
          {isError && (
            <li className="px-4 py-10 text-center text-[13px] text-[var(--fp-tag-red)]">
              The listing could not be loaded.
            </li>
          )}
        </ul>
        {rowActions && rowMenu.element}
        {rowActions && folderMenu.element}
      </>
    );
  }

  const thClass = embedded
    ? flushHeader
      ? TH_EMBEDDED_FLUSH
      : TH_EMBEDDED
    : TH_FLOATING;
  const dated = !reasons;
  // The picker's location is a line under the name and its document date is
  // not a column at all; both are the page's.
  const locationColumn = showLocation && !picker;
  const documentDateColumn = dated && !picker;
  const openColumn = picker;
  const columnCount =
    1 +
    (selection ? 1 : 0) +
    (reasons ? 1 : 0) +
    (recordColumn ? 1 : 0) +
    (locationColumn ? 1 : 0) +
    (documentDateColumn ? 1 : 0) +
    (dated ? 1 : 0) +
    (hideType ? 0 : 1) +
    (rowActions ? 1 : 0) +
    (openColumn ? 1 : 0);
  const headerProps = { sort, onSortChange, sortable: dated, thClass };
  const empty =
    !isLoading && !isError && rows.length === 0 && folders.length === 0;
  // Eight skeleton rows at the real row height while a listing loads with
  // nothing yet to show: the header stays, nothing spins (Claude Design,
  // round 8). A bar at the name's width, shorter bars in the other cells.
  // Held while anything the listing needs is in flight, rows already in hand
  // or not: a browse listing whose files arrive before its folders would
  // otherwise paint the files alone and reshape a beat later (round 9).
  const skeleton = isLoading
    ? Array.from({ length: 8 }, (_, index) => (
        <tr
          key={`skeleton-${index}`}
          data-testid="documents-skeleton-row"
          aria-hidden
          className="border-b border-[var(--fp-border)]"
        >
          {selection && <td className={`${tdClass} w-10 !px-2`} />}
          <td className={tdClass}>
            <span className="my-1 block h-3.5 w-3/5 rounded-[4px] bg-[var(--fp-ink-wash)]" />
          </td>
          {Array.from(
            {
              length:
                columnCount -
                1 -
                (selection ? 1 : 0) -
                (rowActions ? 1 : 0) -
                (openColumn ? 1 : 0),
            },
            (_, cell) => (
              <td key={cell} className={tdClass}>
                <span className="my-1 block h-3.5 w-1/2 rounded-[4px] bg-[var(--fp-ink-wash)]" />
              </td>
            )
          )}
          {rowActions && <td className={tdClass} />}
          {openColumn && <td className={tdClass} />}
        </tr>
      ))
    : null;

  return (
    <>
      <table
        className={`w-full border-collapse ${picker ? "table-fixed" : ""}`}
        aria-busy={isLoading}
      >
        <thead hidden={hideHeaderWhenEmpty && empty}>
          <tr>
            {selection && (
              <th scope="col" className={`${thClass} w-10 !px-2`}>
                {selection.setAll && (
                  <SelectAllCheck
                    rows={rows}
                    selection={selection}
                    setAll={selection.setAll}
                  />
                )}
              </th>
            )}
            <SortableHeader label="Name" column="name" {...headerProps} />
            {reasons && (
              <th scope="col" className={thClass}>
                {reasonsLabel ?? "Reason"}
              </th>
            )}
            {recordColumn && (
              <th scope="col" className={thClass}>
                {recordColumn.label}
              </th>
            )}
            {locationColumn && (
              <th scope="col" className={thClass}>
                Location
              </th>
            )}
            {documentDateColumn && (
              <SortableHeader
                label="Document date"
                column="document_date"
                {...headerProps}
              />
            )}
            {dated && (
              <SortableHeader
                label="Uploaded"
                column="uploaded_at"
                className={picker ? "w-[140px]" : ""}
                {...headerProps}
              />
            )}
            {!hideType && (
              <th
                scope="col"
                className={`${thClass} ${
                  picker ? "w-[160px]" : dense ? "w-[130px]" : "w-[168px]"
                }`}
              >
                Type
              </th>
            )}
            {rowActions && <th scope="col" className={thClass} />}
            {openColumn && (
              <th scope="col" className={`${thClass} w-9 !px-1`} />
            )}
          </tr>
        </thead>
        <tbody
          className={staleClass}
          aria-busy={stale || undefined}
          data-stale={stale ? "true" : undefined}
        >
          {skeleton}
          {!isLoading &&
            folders.map((folder) => {
              const name =
                folderName?.(folder) ??
                folder.filePath[folder.filePath.length - 1] ??
                "";
              const folderSelected =
                selection?.isFolderSelected?.(folder) ?? false;
              return (
                <tr
                  key={`folder:${folder.driveId ?? ""}:${JSON.stringify(folder.filePath)}`}
                  data-selected={folderSelected || undefined}
                  // A selected folder looks selected, exactly as a selected
                  // document does. Wiring folder ticking without this left one
                  // listing showing two treatments: the document washed, the
                  // folder marked only by a tick you had to look for (found in
                  // the round 7h visual pass).
                  className={`group cursor-pointer border-b border-[var(--fp-border)] hover:bg-[var(--fp-hover-row)] ${
                    folderSelected ? "bg-[var(--fp-accent-soft)]" : ""
                  }`}
                  onClick={() =>
                    onOpenFolder(folder.filePath, undefined, folder.driveId)
                  }
                  onContextMenu={
                    rowActions
                      ? (e) => {
                          e.preventDefault();
                          folderMenu.open(targetFromEvent(folder.filePath, e));
                        }
                      : undefined
                  }
                >
                  {selection && (
                    // Quiet until hover, exactly like a document row's — a
                    // folder tick that is always painted while a document's is
                    // revealed makes one listing look like two (round 7h
                    // review).
                    <td
                      className={`${tdClass} w-10 !px-2 ${
                        rowActions && !folderSelected
                          ? "opacity-0 focus-within:opacity-100 group-hover:opacity-100"
                          : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Guarded like the document row's cell: the tick is
                        // disabled on a reserved root, but the cell around it is
                        // 40px of padding, and an unguarded cell selected the
                        // folder the routes then refuse.
                        if (selection.canSelectFolder?.(folder) ?? true)
                          selection.toggleFolder?.(folder);
                      }}
                    >
                      {selection.toggleFolder && (
                        <RowCheck
                          checked={folderSelected}
                          disabled={
                            !(selection.canSelectFolder?.(folder) ?? true)
                          }
                          label={name}
                          onToggle={() => selection.toggleFolder?.(folder)}
                        />
                      )}
                    </td>
                  )}
                  <td
                    className={`${tdClass} w-full max-w-0 ${picker ? "" : "min-w-[12rem]"}`}
                  >
                    <button
                      type="button"
                      aria-label={name}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenFolder(
                          folder.filePath,
                          undefined,
                          folder.driveId
                        );
                      }}
                      className="flex min-w-0 max-w-full items-center gap-2 text-left font-medium"
                    >
                      {folderApp?.(folder) ? (
                        <AppMark app={folderApp(folder)!} />
                      ) : (
                        <FolderIcon
                          size={16}
                          weight="fill"
                          className="shrink-0 text-[var(--fp-ink-3)]"
                          aria-hidden
                        />
                      )}
                      <span className="truncate">{name}</span>
                    </button>
                    {/* A search's folder hit says where it is (#12088): in
                      the picker under the name, exactly where a file hit
                      shows its location; on the page in the Location
                      column below. */}
                    {picker && showLocation && (
                      <span className="block truncate pl-6 text-[12px] leading-4 text-[var(--fp-ink-3)]">
                        {folderTrailOf(folder).join(" › ")}
                      </span>
                    )}
                  </td>
                  {reasons && <td className={tdClass} />}
                  {recordColumn && <td className={tdClass} />}
                  {locationColumn && (
                    <td className={`${tdClass} text-[var(--fp-ink-2)]`}>
                      <LocationCell
                        trail={folderTrailOf(folder)}
                        dense={dense}
                        onOpen={() =>
                          onOpenFolder(
                            folder.filePath.slice(0, -1),
                            undefined,
                            folder.driveId
                          )
                        }
                        onOpenCrumb={(index) => openFolderCrumb(folder, index)}
                      />
                    </td>
                  )}
                  {documentDateColumn && <td className={tdClass} />}
                  {dated && (
                    <td
                      className={`${tdClass} whitespace-nowrap text-[var(--fp-ink-2)]`}
                    >
                      {/* The page shows the folder's newest document date here
                        (CD round 1); a picker's folder row is only its name
                        (round 8). */}
                      {!picker && folder.documentDate
                        ? formatCalendarDay(folder.documentDate, "medium")
                        : ""}
                    </td>
                  )}
                  <td className={tdClass} />
                  {openColumn && <td className={tdClass} />}
                  {rowActions && (
                    <td className={`${tdClass} text-right`}>
                      <button
                        type="button"
                        aria-label={`Actions for ${name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          folderMenu.open(targetFromEvent(folder.filePath, e));
                        }}
                        className="rounded-[6px] p-1 text-[var(--fp-ink-3)] hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)]"
                      >
                        <DotsThreeIcon size={18} weight="bold" aria-hidden />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          {!isLoading &&
            rows.map((row) => {
              const processing = isProcessingFile(toRenderable(row));
              const failed = isFailedFile(toRenderable(row));
              const greyed = isGreyedFile(toRenderable(row));
              const locked = isLocked(row);
              const unreadable = unreadableLabelOf(row);
              // A document inside a ticked folder is selected with it
              // (#12088, Hunter): it reads as selected and cannot be
              // toggled on its own.
              const covering = selection?.coveringFolder?.(row) ?? null;
              const selected =
                (selection?.isSelected(row.fileId) ?? false) ||
                covering !== null;
              const active =
                row.fileId === activeFileId || row.fileId === focusFileId;
              const selectable = selection
                ? rowSelectable(row, selection)
                : true;
              const header = headers?.get(row.fileId);
              const closed =
                groups?.isOpen !== undefined &&
                !groups.isOpen(groups.of(row).key);
              if (closed && !header) return null;
              return (
                <React.Fragment key={row.fileId}>
                  {header && (
                    <tr
                      data-group-header
                      className="border-b border-[var(--fp-border)] bg-[var(--fp-band)]"
                    >
                      <th
                        scope="row"
                        colSpan={columnCount}
                        className="px-3 py-2 text-left font-normal"
                      >
                        <GroupHeading
                          group={header.group}
                          count={header.count}
                          onOpenRecord={groups?.onOpenRecord}
                          open={groups?.isOpen?.(header.group.key)}
                          onToggle={
                            groups?.onToggle
                              ? () => groups.onToggle?.(header.group.key)
                              : undefined
                          }
                          decoration={groups?.decorate?.(
                            header.group,
                            header.rows
                          )}
                        />
                      </th>
                    </tr>
                  )}
                  {!closed && (
                    <tr
                      ref={row.fileId === focusFileId ? focusRef : undefined}
                      data-selected={selected || undefined}
                      data-active={active || undefined}
                      className={`group border-b border-[var(--fp-border)] hover:bg-[var(--fp-hover-row)] ${
                        selected
                          ? "bg-[var(--fp-accent-soft)]"
                          : active
                            ? "bg-[var(--fp-ink-wash)]"
                            : ""
                      } ${openOnRowClick || selection ? "cursor-pointer" : ""}`}
                      onContextMenu={
                        rowActions
                          ? (e) => {
                              e.preventDefault();
                              rowMenu.open(targetFromEvent(row, e));
                            }
                          : undefined
                      }
                      // A row IS the document, so the row has one action and it is
                      // the document's primary action (Claude Design, round 7f).
                      // Before this the larger, unlabelled region opened Details
                      // while the smaller, labelled one opened the document —
                      // exactly inverted. A modified click still means "new tab".
                      onClick={
                        openOnRowClick
                          ? (e) => {
                              if (inLink(e)) return;
                              if (e.metaKey || e.ctrlKey || e.shiftKey)
                                window.open(documentHref(row), "_blank");
                              else onOpenFile(row);
                            }
                          : selection
                            ? () => {
                                if (selectable) selection.toggle(row);
                              }
                            : undefined
                      }
                      // `auxclick` bubbles out of the name like any other event and
                      // the anchor's click handler cannot stop it, so without this
                      // guard a middle click on the name opened the document twice:
                      // once natively, once here (round 7f review B1).
                      onAuxClick={
                        openOnRowClick
                          ? (e) => {
                              if (e.button === 1 && !inLink(e))
                                window.open(documentHref(row), "_blank");
                            }
                          : undefined
                      }
                    >
                      {selection && (
                        <td
                          // The tick column belongs to the tick: without this, a
                          // click on its padding fell through to the row and opened
                          // the document (round 7f review N11).
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectable) selection.toggle(row);
                          }}
                          className={`${tdClass} w-10 !px-2 ${
                            rowActions && !selected
                              ? "opacity-0 focus-within:opacity-100 group-hover:opacity-100"
                              : ""
                          }`}
                        >
                          <CoveredCheck
                            covering={covering}
                            checked={selected}
                            disabled={!selectable}
                            label={displayFileName(row.fileName)}
                            onToggle={() => selection.toggle(row)}
                          />
                        </td>
                      )}
                      <td
                        className={`${tdClass} w-full max-w-0 ${picker ? "" : "min-w-[12rem]"}`}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="shrink-0">
                            {getFileIcon(toRenderable(row), greyed)}
                          </span>
                          {/* An anchor rather than a button: it stays the row's
                        focusable, accessibly-named element, and the browser
                        gives modified clicks their new tab for nothing.

                        Only where the row opens the document, though. A picker
                        is mounted over some other page, so an href built from
                        the current URL would name an address that ignores
                        `docId` — a link whose status-bar target is a lie on
                        every row (round 7f review N2). */}
                          {picker ? (
                            // The row is the tick and the name is only the name
                            // (Claude Design, round 8): no link, no underline, no
                            // second target. Open is the button at the row's end.
                            <span
                              className={`min-w-0 truncate ${
                                greyed
                                  ? "text-[var(--fp-ink-3)]"
                                  : "font-medium"
                              }`}
                            >
                              {displayFileName(row.fileName)}
                            </span>
                          ) : openOnRowClick ? (
                            <a
                              href={documentHref(row)}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (e.metaKey || e.ctrlKey || e.shiftKey)
                                  return;
                                e.preventDefault();
                                onOpenFile(row);
                              }}
                              className={`min-w-0 truncate text-left hover:underline ${
                                greyed
                                  ? "text-[var(--fp-ink-3)]"
                                  : "font-medium"
                              }`}
                            >
                              {displayFileName(row.fileName)}
                            </a>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenFile(row);
                              }}
                              className={`min-w-0 truncate text-left hover:underline ${
                                greyed
                                  ? "text-[var(--fp-ink-3)]"
                                  : "font-medium"
                              }`}
                            >
                              {displayFileName(row.fileName)}
                            </button>
                          )}
                          {isLocked(row) && (
                            // The passive mark on the name's line (round 12,
                            // #11856): it says the file is locked and nothing
                            // else; opening the file is where it is unlocked.
                            <span
                              data-testid="row-lock"
                              className="inline-flex shrink-0 items-center leading-none"
                            >
                              <LockedFileIndicator
                                status={row.status ?? undefined}
                              />
                            </span>
                          )}
                          {row.unreadableReason && (
                            // A file we cannot read says why on its name's
                            // line (#11277); a password does not open it, so
                            // the mark is not the lock's unlock path.
                            <span
                              data-testid="row-unreadable"
                              data-reason={row.unreadableReason}
                              className="inline-flex shrink-0 items-center leading-none"
                            >
                              <UnreadableFileIndicator
                                reason={row.unreadableReason}
                                label={unreadableLabelOf(row) ?? ""}
                              />
                            </span>
                          )}
                        </span>
                        {/* The line under the name: the custom value a search
                      matched when the name did not (round 8b) — "never a
                      bare row you can't explain" — else, in the picker's
                      recursive listings, the location, never a column
                      (Claude Design, round 8). */}
                        {row.matchedAttribute ? (
                          <span
                            data-testid="row-matched-attribute"
                            className="block truncate pl-6 text-[12px] leading-4 text-[var(--fp-ink-3)]"
                          >
                            {row.matchedAttribute.columnName}:{" "}
                            {row.matchedAttribute.value}
                          </span>
                        ) : (
                          picker &&
                          showLocation && (
                            <span
                              data-testid="row-location"
                              className="block truncate pl-6 text-[12px] leading-4 text-[var(--fp-ink-3)]"
                            >
                              {trailOf(row).join(" › ")}
                            </span>
                          )
                        )}
                      </td>
                      {reasons && (
                        <td
                          className={`${tdClass} max-w-[220px] text-[var(--fp-ink-2)]`}
                        >
                          <CutText
                            text={reasons[row.fileId] ?? ""}
                            className="block truncate"
                          />
                        </td>
                      )}
                      {recordColumn && (
                        <td className={`${tdClass} max-w-[220px] truncate`}>
                          <RecordChips
                            {...recordColumn.of(row)}
                            onOpenRecord={onOpenRecord}
                          />
                        </td>
                      )}
                      {locationColumn && (
                        <td className={`${tdClass} text-[var(--fp-ink-2)]`}>
                          <LocationCell
                            trail={trailOf(row)}
                            dense={dense}
                            onOpen={() => onOpenFolder(row.filePath)}
                            // Crumb i is the place (0) or the i-th folder under
                            // its root: the path's head, the root's length plus i.
                            onOpenCrumb={(index) =>
                              onOpenFolder(
                                row.filePath.slice(
                                  0,
                                  row.filePath.length -
                                    (trailOf(row).length - 1) +
                                    index
                                )
                              )
                            }
                          />
                        </td>
                      )}
                      {documentDateColumn && (
                        <td
                          className={`${tdClass} whitespace-nowrap text-[var(--fp-ink-2)]`}
                        >
                          {row.documentDate
                            ? formatCalendarDay(row.documentDate, "medium")
                            : EM_DASH}
                        </td>
                      )}
                      {dated && (
                        <td
                          className={`${tdClass} whitespace-nowrap text-[var(--fp-ink-2)]`}
                        >
                          {picker ? (
                            // The date alone: My uploads is the chip that answers
                            // who (Claude Design, round 8).
                            row.uploadedAt ? (
                              formatInstant(row.uploadedAt, zone, "medium")
                            ) : (
                              EM_DASH
                            )
                          ) : dense && row.uploadedBy ? (
                            <Tooltip
                              title={row.uploadedBy.label}
                              placement="top"
                            >
                              <span>
                                {row.uploadedAt
                                  ? formatInstant(
                                      row.uploadedAt,
                                      zone,
                                      "medium"
                                    )
                                  : EM_DASH}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="flex items-center">
                              {row.uploadedAt
                                ? formatInstant(row.uploadedAt, zone, "medium")
                                : EM_DASH}
                              {row.uploadedBy && (
                                <span className="flex min-w-0 text-[var(--fp-ink-3)]">
                                  <span className="px-1">·</span>
                                  <UploaderName uploader={row.uploadedBy} />
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                      )}
                      {!hideType && (
                        <td
                          className={`${tdClass} ${
                            picker
                              ? "max-w-[160px]"
                              : dense
                                ? "max-w-[130px]"
                                : "max-w-[168px]"
                          } overflow-hidden`}
                        >
                          {picker && (processing || failed) && !locked ? (
                            // An in-flight row says so where its type would go, the
                            // raw state behind a tooltip (Claude Design, round 8);
                            // a failed one says its processing failed, in the same
                            // quiet treatment (#13887).
                            <Tooltip
                              title={row.status ?? "processing"}
                              placement="top"
                            >
                              <span className="text-[var(--fp-ink-3)]">
                                {failed ? "Processing failed" : "Processing"}
                              </span>
                            </Tooltip>
                          ) : unreadable ? (
                            // A file we cannot read has no type to show; its
                            // reason goes where the type would (#11277).
                            <span
                              data-testid="row-unreadable-reason"
                              title={unreadable}
                              className="block truncate text-[var(--fp-ink-3)]"
                            >
                              {unreadable}
                            </span>
                          ) : (
                            <TypeTags
                              types={row.documentTypes}
                              typeLabels={typeLabels}
                            />
                          )}
                        </td>
                      )}
                      {openColumn && (
                        <td className={`${tdClass} !px-1 text-right`}>
                          {/* The side door: the viewer in a new tab, so the modal
                        and its selection survive (Claude Design, round 8). */}
                          <Hint label="Open">
                            <a
                              href={documentPageHref(row)}
                              target="_blank"
                              rel="noopener"
                              aria-label={`Open ${displayFileName(row.fileName)}`}
                              onClick={(e) => e.stopPropagation()}
                              className={`inline-flex ${quickActionClass}`}
                            >
                              <ArrowSquareOutIcon size={16} aria-hidden />
                            </a>
                          </Hint>
                        </td>
                      )}
                      {rowActions && (
                        <td className={`${tdClass} text-right`}>
                          {/* Drive's hover strip: the two things done most, quiet
                        until the row is hovered, then the ⋮ for the rest. */}
                          <span className="inline-flex items-center gap-0.5">
                            {/* Three controls, three distinct actions, none of
                          them the row's own. The popout that used to sit
                          here opened the document — a second control for the
                          action the row already carries, 900px from the
                          first — and its slot went to Details, which was
                          previously reachable only by accident. */}
                            {!dense && onDetails && (
                              <Hint label="Details">
                                <button
                                  type="button"
                                  aria-label="Details"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDetails(row);
                                  }}
                                  className={quickActionClass}
                                >
                                  <InfoIcon size={16} aria-hidden />
                                </button>
                              </Hint>
                            )}
                            {!dense && (
                              <Hint label="Download">
                                <button
                                  type="button"
                                  aria-label="Download"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void downloadDocument(row);
                                  }}
                                  className={quickActionClass}
                                >
                                  <DownloadSimpleIcon size={16} aria-hidden />
                                </button>
                              </Hint>
                            )}
                            <Hint label="More" surfaceOpen={rowMenu.isOpen}>
                              <button
                                type="button"
                                aria-label="More"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  rowMenu.open(targetFromEvent(row, e));
                                }}
                                className="rounded-[6px] p-1 text-[var(--fp-ink-3)] hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)]"
                              >
                                <DotsThreeIcon
                                  size={18}
                                  weight="bold"
                                  aria-hidden
                                />
                              </button>
                            </Hint>
                          </span>
                        </td>
                      )}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          {empty && (
            <tr>
              <td
                colSpan={columnCount}
                className="px-3 py-10 text-center text-[13px] text-[var(--fp-ink-3)]"
              >
                {emptyState ?? "No documents"}
              </td>
            </tr>
          )}
          {isError && (
            <tr>
              <td
                colSpan={columnCount}
                className="px-3 py-10 text-center text-[13px] text-[var(--fp-tag-red)]"
              >
                The listing could not be loaded.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rowActions && rowMenu.element}
      {rowActions && folderMenu.element}
    </>
  );
};

export default DocumentsTable;
